<script setup lang="ts">
import { useDynamicDialog } from '@/shared/composables/UseDynamicDialog'
import CreateSalesDailyLogDialog from '@/features/sales/dialogs/CreateSalesDailyLogDialog.vue'
import EditSalesDailyLogDialog from '@/features/sales/dialogs/EditSalesDailyLogDialog.vue'
import DateRangePicker from '@/shared/components/form/DateRangePicker.vue'
import DemoDataTable from '@/features/sales/components/DemoDataTable.vue'
import SalesPeriodOverviewTable from '@/features/sales/components/SalesPeriodOverviewTable.vue'
import MySalesGoalsCard from '@/features/sales/components/MySalesGoalsCard.vue'
import { useAuthStore } from '@/shared/stores/AuthStore'
import { storeToRefs } from 'pinia'
import { buildStatisticsRangePresets, endOfIsoWeek, startOfIsoWeek, toDateOnlyString } from '@/features/sales/composables/useSalesMetricsAdapter'
import {
  useAdminListMySalesDailyLogs,
  useAdminGetMySalesStats,
  useListAdminUsers,
} from '@swipe4work/api-orval'
import type {
  AdminListMySalesDailyLogsParams,
  ListSalesDailyLogsSalesDailyLogDto,
} from '@swipe4work/models-orval'
import { AdminPermissionResource, PermissionLevel } from '@swipe4work/models-orval'
import { usePermissions } from '@/shared/composables/UsePermissions'

definePage({
  path: '/sales/sales-metrics/my-logs',
  name: '/sales/SalesMetrics/my-logs',
  meta: {
    permission: {
      resource: 'SalesMetrics',
      level: 'Read',
    },
  },
})

const dialog = useDynamicDialog()
const { can } = usePermissions()
const authStore = useAuthStore()
const { user } = storeToRefs(authStore)
const rangePresets = buildStatisticsRangePresets()
const today = new Date()

const dateRange = ref<{
  from: Date | null,
  to: Date | null
} | null>({
  from: startOfIsoWeek(today),
  to: endOfIsoWeek(today),
})

type LogFilterState = {
  logDate: {
    from: Date | null
    to: Date | null
  } | null
}

const {
  sortField,
  sortDescending,
  offset,
  rows,
  ROWS_PER_PAGE_OPTIONS,
  onSort,
  onPage,
} = useDataTableFilters<LogFilterState>({
  schema: { logDate: 'dateRange' },
  defaultFilters: () => ({ logDate: null }),
  persistToStorage: 'sales-my-logs',
  defaultPageSize: 10,
  defaultSortField: 'date',
  defaultSortDescending: true,
})

const { data: adminUsersData } = useListAdminUsers({ query: { staleTime: 5 * 60 * 1000 } })

const currentSalesPersonId = computed(() => {
  if (!user.value?.localAccountId) return null

  return adminUsersData.value?.users.find(u => u.externalId === user.value?.localAccountId)?.id ?? null
})

const dateFromParam = computed(() => dateRange.value?.from ? (toDateOnlyString(dateRange.value.from) as unknown as Date) : undefined)
const dateToParam = computed(() => dateRange.value?.to ? (toDateOnlyString(dateRange.value.to) as unknown as Date) : undefined)

const logsParams = computed<AdminListMySalesDailyLogsParams>(() => ({
  dateFrom: dateFromParam.value,
  dateTo: dateToParam.value,
  sortField: sortField.value,
  sortDescending: sortDescending.value,
  limit: rows.value,
  offset: offset.value,
}))

const statsParams = computed(() => ({
  dateFrom: dateFromParam.value,
  dateTo: dateToParam.value,
}))

const { data: logsData, isPending: isLoadingLogs, isError: isLogsError } = useAdminListMySalesDailyLogs(logsParams)
const { data: statsData, isPending: isLoadingStats } = useAdminGetMySalesStats(statsParams)

const logs = computed<ListSalesDailyLogsSalesDailyLogDto[]>(() => logsData.value?.logs ?? [])
const totalCount = computed(() => logsData.value?.totalCount ?? 0)
const stats = computed(() => statsData.value)

const formatDate = (date: Date | string) =>
  new Intl.DateTimeFormat('nl-NL', { dateStyle: 'short' }).format(new Date(date))

function openCreateDialog() {
  dialog.open(CreateSalesDailyLogDialog, {
    props: {
      modal: true,
      closable: true,
      showHeader: false,
      dismissableMask: true,
      style: {
        width: 'auto',
        height: 'auto',
      },
    },
  })
}

function openEditDialog(logId: number) {
  dialog.open(EditSalesDailyLogDialog, {
    props: {
      modal: true,
      closable: true,
      showHeader: false,
      dismissableMask: true,
      style: {
        width: 'auto',
        height: 'auto',
      },
    },
    data: { logId },
  })
}

const menu = ref()
const selectedLog = ref<ListSalesDailyLogsSalesDailyLogDto | null>(null)

const menuItems = computed(() => [
  {
    label: 'Wijzig log',
    icon: can(AdminPermissionResource.SalesMetrics, PermissionLevel.Write) ? 'fa-regular fa-pen-to-square' : 'fa-regular fa-lock',
    disabled: !can(AdminPermissionResource.SalesMetrics, PermissionLevel.Write),
    command: () => {
      if (selectedLog.value) openEditDialog(selectedLog.value.id)
    },
  },
])

function handleClickRow(event: any, log: ListSalesDailyLogsSalesDailyLogDto) {
  selectedLog.value = log
  menu.value?.show(event.originalEvent ?? event)
}
</script>

<template>
  <ContextMenu
    ref="menu"
    :model="menuItems"
    @hide="selectedLog = null"
  />

  <div class="space-y-4">
    <div class="flex flex-wrap items-end justify-between gap-4">
      <div class="flex flex-col gap-1">
        <label class="text-xs text-surface-500 font-medium tracking-wide uppercase">Periode</label>
        <DateRangePicker v-model="dateRange" :presets="rangePresets" placeholder="Alle data" />
      </div>
      <Button
        v-permission="{ resource: AdminPermissionResource.SalesMetrics, level: PermissionLevel.Write }"
        label="Nieuwe Log"
        icon="fa-regular fa-plus"
        @click="openCreateDialog"
      />
    </div>

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <template v-if="isLoadingStats">
        <Skeleton v-for="n in 3" :key="n" height="5rem" />
      </template>
      <template v-else>
        <StatTile
          label="Gebeld"
          :value="String(stats?.totalCalledCompanies ?? 0)"
          :subtitle="stats ? `${stats.callToConversationRate.toFixed(1)}% conversie` : ''"
        />
        <StatTile
          label="Gesprekken"
          :value="String(stats?.totalConversations ?? 0)"
          :subtitle="stats ? `${stats.conversationToDemoRate.toFixed(1)}% naar outbound demo` : ''"
        />
        <StatTile
          label="Demo's"
          :value="String(stats?.totalDemos ?? 0)"
          :subtitle="stats ? `${stats.totalOutboundDemos} outbound` : ''"
        />
      </template>
    </div>

    <Card>
      <template #content>
        <div class="flex flex-col gap-4">
          <h3 class="text-lg font-semibold">
            Mijn sales logs
          </h3>
          <div v-if="isLoadingLogs" class="flex flex-col gap-2">
            <Skeleton v-for="n in 5" :key="n" height="3rem" />
          </div>
          <Message v-else-if="isLogsError" severity="error">
            Er is iets misgegaan bij het laden van logs.
          </Message>
          <DataTable
            v-else
            :value="logs"
            :lazy="true"
            :first="offset"
            :rows="rows"
            :rows-per-page-options="ROWS_PER_PAGE_OPTIONS"
            :total-records="totalCount"
            :sort-field="sortField"
            :sort-order="sortDescending ? -1 : 1"
            paginator
            paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
            current-page-report-template="{first}-{last} van {totalRecords}"
            size="small"
            class="w-full"
            row-hover
            @row-click="(e) => handleClickRow(e, e.data)"
            @page="onPage"
            @sort="onSort"
          >
            <template #empty>
              <span class="text-sm text-surface-400">Nog geen logs gevonden. Maak je eerste log aan!</span>
            </template>
            <Column field="date" header="Datum" sortable style="width: 8rem">
              <template #body="{ data: log }">
                {{ formatDate(log.date) }}
              </template>
            </Column>
            <Column field="calledCompanies" header="Gebeld" sortable style="width: 6rem" />
            <Column field="conversations" header="Gesprekken" sortable style="width: 7rem" />
            <Column field="demoCount" header="Demo's" sortable style="width: 6rem" />
            <Column field="outboundDemoCount" header="Outbound" style="width: 7rem" />
            <Column header="Conv. Bel→Gesprek" sortable field="callToConversationRate" style="width: 10rem">
              <template #body="{ data: log }">
                <span class="text-sm font-mono">{{ log.callToConversationRate.toFixed(1) }}%</span>
              </template>
            </Column>
            <Column header="Conv. Gesprek→Outbound demo" sortable field="conversationToDemoRate" style="width: 12rem">
              <template #body="{ data: log }">
                <span class="text-sm font-mono">{{ log.conversationToDemoRate.toFixed(1) }}%</span>
              </template>
            </Column>
          </DataTable>
        </div>
      </template>
    </Card>

    <div class="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <MySalesGoalsCard :sales-person-id="currentSalesPersonId" />
      <SalesPeriodOverviewTable :stats="stats" default-group-by="week" />
    </div>

    <Card>
      <template #content>
        <DemoDataTable
          :key="currentSalesPersonId ?? 'pending'"
          :default-sales-person-id="currentSalesPersonId"
          :show-sales-person-column="false"
          :show-sales-person-filter="false"
          storage-key="sales-my-demos"
        />
      </template>
    </Card>
  </div>
</template>
